import React, { useEffect } from 'react';
import { ReactNode } from 'react';
import { inject, observer } from 'mobx-react';
import { IRichTextItemInternal, IRichTextItemsInternal, NodeType, IAttributeInfo } from '../stores/types';
import classNames from 'classnames';
import { MainStore } from '../stores/MainStore';
import styles from './richText.scss';
import { decodeHTML } from './html-helper';

interface IRichTextProps {
    store?: MainStore;
    richTextId: string;
    injectedRichTexts?: IRichTextItemsInternal[];
}
export const RichText = inject('store')(
    observer((props: IRichTextProps) => {
        const refRTF = React.useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (refRTF.current) {
                const links = refRTF.current.querySelectorAll('.pdp-richtext_line_a');

                if (links && links.length > 0) {
                    links.forEach(link => {
                        let currentUrl = link.getAttribute('href');

                        if(!currentUrl) return;
                        
                        if (currentUrl.includes('itemorigin')) {
                            const urlParamsArray = currentUrl.split('?');
                            const placeOfTag = urlParamsArray.findIndex(it => it.includes('itemorigin='));
                            urlParamsArray[placeOfTag] = store?.productInfoItemOrigin.substring(1) || '';
                            currentUrl = urlParamsArray.join('?');
                        } else {
                            currentUrl = currentUrl.concat(store?.productInfoItemOrigin || '');
                        }

                        link.setAttribute('href', currentUrl);
                    });
                }
            }

        }, []);

        const {
            store,
            richTextId,
            injectedRichTexts,
        } = props;
        const injectedRichTextExist = injectedRichTexts && injectedRichTexts.length;
        const allRichTexts = injectedRichTextExist ? props.injectedRichTexts : store?.fetchedData.allRichTexts;
        
        if(!allRichTexts) return <></>;
        
        const richTextItemsSelected: IRichTextItemInternal[] | undefined 
            = allRichTexts.find(rit=>rit.id === richTextId)?.items;

        if(!richTextItemsSelected || !richTextItemsSelected.length) return <></>;

        function MapLines(richTextItems: IRichTextItemInternal[]): ReactNode {
            return richTextItems.map((x, index)=> MapLine(x, index));
        }

        function MapLine(
            richTextItem: IRichTextItemInternal,
            index: number
        ): ReactNode {
            const semanticProps = (type: string) => ({
                'data-testid': `${type}_type_elm`,
                'data-semantic-id': `${type}_type_elm`,
            });

            const link = richTextItem.link;

            switch (richTextItem.nodeType) {
                case NodeType.LineBreak:
                    return (
                        <br
                            key={index}
                            {...semanticProps('br')}
                        />
                    );

                case NodeType.BlankLine:
                    return (
                        <div
                            key={index}
                            className={classNames(
                                getClasses(richTextItem.markups, true, false)
                            )}
                            {...semanticProps('bl')}
                        />
                    );

                case NodeType.Paragraph:
                    return (
                        <div
                            key={index}
                            className={classNames(
                                getClasses(richTextItem.markups, false, false)
                            )}
                            {...semanticProps('paragraph')}
                        >
                            {MapLines(richTextItem.children)}
                        </div>
                    );

                case NodeType.List:
                    return (
                        <ul
                            key={index}
                            className="pdp-richtext_list"
                            {...semanticProps('ul')}
                        >
                            {MapLines(richTextItem.children)}
                        </ul>
                    );

                case NodeType.Text: {
                    const text = decodeHTML(richTextItem.textValue);
                    const className = classNames(
                        getClasses(richTextItem.markups, true, !!link?.href)
                    );

                    if (link?.href) {
                        return (
                            <a
                                key={index}
                                className={className}
                                href={link.href}
                                {...(link.openInNewTab
                                    ? {
                                        target: '_blank',
                                        rel: 'noreferrer',
                                    }
                                    : {})}
                                {...semanticProps('a')}
                                ref={(element) =>
                                    addAttributes(element, richTextItem.attributes)
                                }
                            >
                                {text}
                            </a>
                        );
                    }

                    return (
                        <span
                            key={index}
                            className={className}
                            {...semanticProps('span')}
                            ref={(element) =>
                                addAttributes(element, richTextItem.attributes)
                            }
                        >
                            {text}
                        </span>
                    );
                }

                case NodeType.ListItem: {
                    const children = richTextItem.children?.length
                        ? MapLines(richTextItem.children)
                        : null;

                    const text = decodeHTML(richTextItem.textValue);
                    const className = classNames(
                        getClasses(richTextItem.markups, true, false)
                    );

                    const content = link?.href ? (
                        <a
                            className={styles.richtext_line_a}
                            href={link.href}
                            {...(link.openInNewTab
                                ? {
                                    target: '_blank',
                                    rel: 'noreferrer',
                                }
                                : {})}
                            {...semanticProps('a')}
                        >
                            {text}
                            {children}
                        </a>
                    ) : (
                        <>
                            {text}
                            {children}
                        </>
                    );

                    return (
                        <li
                            key={index}
                            className={className}
                            {...semanticProps('li')}
                        >
                            {content}
                        </li>
                    );
                }

                default:
                    return null;
            }
        }

        function getClasses(
            markups: string[],
            extendWithBulletStyle: boolean,
            extendWithAnchorStyle: boolean): string[] {

            let classes: string[] = [];
            if (extendWithBulletStyle)
                classes = classes.concat('pdp-richtext_bullet');

            if (markups && markups.length) {
                classes = classes.concat(markups.map(x=>'pdp-richtext_'+x))
            }

            if (extendWithAnchorStyle)
                classes = classes.concat(styles.richtext_line_a);

            return classes;
        }

        function addAttributes(
            el: HTMLElement | null, attributes: IAttributeInfo[] | null): void {

            if (el && attributes && attributes.length) {
                attributes.forEach((attribute) => 
                    attribute.name && el.setAttribute(attribute.name, attribute.value)
                );
            }
        }
        
        return (
            <div
                className = {styles.richtext_root}
                data-testid='richtext_root'
                data-semantic-id='richtext_root'
                ref={refRTF}
            >
                {MapLines(richTextItemsSelected)}
            </div>
        )
}));

